import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import AvatarCropModal from '../components/AvatarCropModal';
import MonetizationPlanModal from '../components/MonetizationPlanModal';
import SubscriptionSettings from '../components/SubscriptionSettings';
import RevShareSettings from '../components/RevShareSettings';
import MessageCenter from '../components/MessageCenter';
import ProfileCompletionBanner from '../components/ProfileCompletionBanner';
import SetupChecklistCard from '../components/SetupChecklistCard';
import VisibilityAndPreviewCard from '../components/VisibilityAndPreviewCard';
import { getAvatarUrl } from '../utils/avatarUtils';
import { isProfileComplete, getMissingFields, MIN_BIO_LENGTH, MAX_BIO_LENGTH, canStartSessions } from '../utils/profileCompletion';
import ReviewerGuidedTour from '../components/ReviewerGuidedTour';
import '../styles/ReviewerDashboard.css';
import {
  User,
  Settings,
  Mail,
  Bell,
  Calendar,
  DollarSign,
  Star,
  Play,
  Music,
  CheckCircle,
  Radio,
  Upload,
  Edit2,
  Check,
  X,
  Trash2,
  AlertTriangle,
  Link,
  Lock,
  ChevronDown,
  ChevronUp,
  Download,
  Users,
  Crown,
  Clock
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import { isAuthenticated } from '../utils/auth';
import { safeCurrency, safeNumber } from '../utils/format';

const BACKEND_URL = '';  // Use relative URLs with proxy

const ReviewerDashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const [user, setUser] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [collectedEmails, setCollectedEmails] = useState([]);
  const [emailsLoading, setEmailsLoading] = useState(false);
  const [emailsStats, setEmailsStats] = useState({ total_unique_emails: 0, total_submissions: 0 });
  const [emailsCanExport, setEmailsCanExport] = useState(false);
  const [emailsPage, setEmailsPage] = useState(1);
  const EMAILS_PER_PAGE = 10;
  const [loading, setLoading] = useState(true);
  const [activeSession, setActiveSession] = useState(null);
  const [isAuthed, setIsAuthed] = useState(false); // Auth guard state
  
  // Refs for profile completion scroll-to functionality
  const avatarSectionRef = useRef(null);
  const displayNameRef = useRef(null);
  const bioRef = useRef(null);
  const bioTabRef = useRef(null);
  
  // Pagination state for session history
  const [sessionPage, setSessionPage] = useState(1);
  const [sessionTotal, setSessionTotal] = useState(0);
  const [sessionHasMore, setSessionHasMore] = useState(false);
  const [loadingMoreSessions, setLoadingMoreSessions] = useState(false);
  
  // Tab navigation state
  const [activeTab, setActiveTab] = useState('bio');
  
  // Session rename state
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [editingSessionName, setEditingSessionName] = useState('');
  
  // Bio state
  const [bio, setBio] = useState('');
  const [genres, setGenres] = useState([]);
  const [avatar, setAvatar] = useState('');
  const [cardImagePosition, setCardImagePosition] = useState('center'); // For rectangular card cropping
  
  // Image upload state
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [selectedImageForCrop, setSelectedImageForCrop] = useState(null);
  const [showMonetizationModal, setShowMonetizationModal] = useState(false);
  const [monetizationPlan, setMonetizationPlan] = useState(null);
  
  // Notification state
  const [sendingNotification, setSendingNotification] = useState(false);
  const [notificationSettings, setNotificationSettings] = useState({
    auto_notify_on_session_start: false,
    include_queue_count: true,
    custom_sms_message: '',
    custom_email_message: '',
    custom_email_subject: ''
  });
  const [, /* setLoadingNotificationSettings */ ] = useState(false);
  const [savingCustomMessage, setSavingCustomMessage] = useState(false);
  const [customMessagesExpanded, setCustomMessagesExpanded] = useState(false); // Collapsible dropdown state
  const [submissionTypesExpanded, setSubmissionTypesExpanded] = useState(false); // Submission Types dropdown
  const [submissionGatesExpanded, setSubmissionGatesExpanded] = useState(false); // Submission Gates dropdown
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  
  // Social links state
  const [socialLinks, setSocialLinks] = useState({
    cash_app: '',
    venmo: '',
    twitch: '',
    tiktok: '',
    instagram: '',
    spotify_link: '',
    soundcloud: '',
    youtube: ''
  });

  // Settings state
  const [settings, setSettings] = useState({
    allowSpotify: true,
    allowSoundcloud: true,
    allowYoutube: true,
    allowFileUploads: true,
    requireEmail: false,
    requireSpotifyFollow: false,
    skipLineEnabled: true,
    tipsEnabled: true,
    twitchSubSkip: false,
    isAvailable: false
  });
  
  // Pro feature handler - shows coming soon toast
  const handleProFeatureToggle = (featureName) => {
    toast({
      title: "🔒 Pro Feature",
      description: `${featureName} is a Pro feature coming soon!`,
      duration: 3000
    });
  };

  // Pro upgrade banner state - shown after abandoned checkout
  const [showProUpgradeBanner, setShowProUpgradeBanner] = useState(false);
  
  // Check for pro upgrade banner on mount
  useEffect(() => {
    const shouldShowBanner = localStorage.getItem('show_pro_upgrade_banner') === 'true';
    if (shouldShowBanner) {
      setShowProUpgradeBanner(true);
    }
  }, []);
  
  // Dismiss pro upgrade banner
  const dismissProUpgradeBanner = () => {
    setShowProUpgradeBanner(false);
    localStorage.removeItem('show_pro_upgrade_banner');
  };
  
  // Handle upgrade to Pro from banner
  const handleUpgradeFromBanner = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${BACKEND_URL}/api/monetization/select-plan`,
        { plan: 'subscription_cashapp_venmo' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.requires_checkout && response.data.checkout_url) {
        // Remove the banner flag before redirecting
        localStorage.removeItem('show_pro_upgrade_banner');
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Error upgrading to Pro:', error);
      toast({
        title: 'Error',
        description: 'Failed to start upgrade. Please try again.',
        variant: 'destructive'
      });
    }
  };

  // Spotify connection status
  const [spotifyConnected, setSpotifyConnected] = useState(false);
  const [checkingSpotifyStatus, setCheckingSpotifyStatus] = useState(false);

  // Debug tools - reserved for future use
  // eslint-disable-next-line no-unused-vars
  const [debugToolsEnabled, setDebugToolsEnabled] = useState(false);

  const handleConnectStripeFromSetup = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${BACKEND_URL}/api/monetization/stripe/create-account-link`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Error connecting Stripe:', error);
      toast({
        title: 'Error',
        description: 'Failed to connect Stripe. Please try again.',
        variant: 'destructive'
      });
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Auth guard: Check if user is authenticated
        const hasToken = isAuthenticated();
        setIsAuthed(hasToken);
        
        if (!hasToken) {
          console.warn('⚠️ No authentication token found');
          setLoading(false);
          return; // Don&apos;t make API calls without token
        }
        
        const token = localStorage.getItem('access_token');
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        
        // Fetch latest user data from backend to ensure we have current avatar
        if (token && storedUser.id) {
          try {
            const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            
            // Update localStorage with fresh data
            const freshUser = response.data;
            localStorage.setItem('user', JSON.stringify(freshUser));
            
            // Use fresh data
            setUser(freshUser);
            setDisplayName(freshUser.name || '');
            setBio(freshUser.bio || '');
            setGenres(freshUser.genres || []);
            setUsername(freshUser.username || '');
            setEmail(freshUser.email || '');
            setCardImagePosition(freshUser.card_image_position || 'center');
            
            // Use SINGLE CANONICAL avatarUrl with aggressive cache-busting
            const finalAvatarUrl = getAvatarUrl(freshUser);
            
            console.log('✅ Loaded fresh user data from backend');
            console.log('avatarUrl from server:', freshUser.avatarUrl);
            console.log('avatar_updated_at:', freshUser.avatar_updated_at);
            console.log('Final avatar URL:', finalAvatarUrl);
            
            setAvatar(finalAvatarUrl || '');
            
            setSocialLinks({
              cash_app: freshUser.cash_app || '',
              venmo: freshUser.venmo || '',
              twitch: freshUser.twitch || '',
              tiktok: freshUser.tiktok || '',
              instagram: freshUser.instagram || '',
              spotify_link: freshUser.spotify_link || '',
              soundcloud: freshUser.soundcloud || '',
              youtube: freshUser.youtube || ''
            });
            
            // Set isAvailable based on user&apos;s is_live status
            setSettings(prev => ({ ...prev, isAvailable: freshUser.is_live || false }));
            
            // Fetch reviewer stats (total_sessions, etc.) for profile completion check
            try {
              const statsResponse = await axios.get(`${BACKEND_URL}/api/reviewers/me/stats`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              const stats = statsResponse.data;
              // Merge stats into user object for SetupChecklistCard
              const userWithStats = { ...freshUser, ...stats };
              setUser(userWithStats);
              localStorage.setItem('user', JSON.stringify(userWithStats));
              console.log('✅ Loaded reviewer stats:', stats);
            } catch (statsError) {
              console.log('Could not load reviewer stats:', statsError);
            }
            
            // Load submission settings from backend
            try {
              const settingsResponse = await axios.get(`${BACKEND_URL}/api/reviewers/submission-settings`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              const savedSettings = settingsResponse.data;
              setSettings(prev => ({
                ...prev,
                isAvailable: freshUser.is_live || false,
                skipLineEnabled: savedSettings.skip_line_enabled ?? true,
                tipsEnabled: savedSettings.tips_enabled ?? true,
                allowSpotify: savedSettings.allow_spotify ?? false,
                allowSoundcloud: savedSettings.allow_soundcloud ?? false,
                allowYoutube: savedSettings.allow_youtube ?? false,
                allowFileUploads: savedSettings.allow_file_uploads ?? false,
                requireEmail: savedSettings.require_email ?? false,
                requireSpotifyFollow: savedSettings.require_spotify_follow ?? false,
                twitchSubSkip: savedSettings.twitch_sub_skip ?? false
              }));
            } catch (settingsError) {
              console.log('Could not load submission settings, using defaults');
            }
          } catch (error) {
            console.error('Failed to fetch fresh user data:', error);
            // Fall back to localStorage BUT sanitize monetization_plan for safety
            // Clear monetization_plan from stale localStorage to prevent Cash App/Venmo showing incorrectly
            const sanitizedUser = { ...storedUser };
            // Don't trust localStorage for monetization_plan - force a re-check from server
            // If we can't verify with server, default to free plan (safest option)
            if (sanitizedUser.monetization_plan === 'subscription_cashapp_venmo') {
              console.warn('⚠️ Cannot verify PRO plan status from server, defaulting to free plan display');
              sanitizedUser.monetization_plan = 'free_stripe_10percent';
            }
            setupFromLocalStorage(sanitizedUser);
          }
        } else {
          // No stored user ID - use localStorage but sanitize monetization_plan
          const sanitizedUser = { ...storedUser };
          if (sanitizedUser.monetization_plan === 'subscription_cashapp_venmo') {
            console.warn('⚠️ Cannot verify PRO plan status, defaulting to free plan display');
            sanitizedUser.monetization_plan = 'free_stripe_10percent';
          }
          setupFromLocalStorage(sanitizedUser);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error in fetchData:', error);
        setLoading(false);
      }
    };

    const setupFromLocalStorage = (storedUser) => {
      setUser(storedUser);
      setDisplayName(storedUser.name || '');
      setBio(storedUser.bio || '');
      setGenres(storedUser.genres || []);
      setUsername(storedUser.username || '');
      setEmail(storedUser.email || '');
      setCardImagePosition(storedUser.card_image_position || 'center');
      
      // Use SINGLE CANONICAL avatarUrl (getAvatarUrl rejects legacy /api/uploads/ paths)
      const finalAvatarUrl = getAvatarUrl(storedUser);
      // Only log if there&apos;s an actual avatar URL
      if (finalAvatarUrl) {
        console.log('Avatar URL loaded:', finalAvatarUrl);
      }
      setAvatar(finalAvatarUrl || '');
      
      setSocialLinks({
        cash_app: storedUser.cash_app || '',
        venmo: storedUser.venmo || '',
        twitch: storedUser.twitch || '',
        tiktok: storedUser.tiktok || '',
        instagram: storedUser.instagram || '',
        spotify_link: storedUser.spotify_link || '',
        soundcloud: storedUser.soundcloud || '',
        youtube: storedUser.youtube || ''
      });
    };

    fetchData();
  }, []);

  // Fetch notification settings when user is loaded
  useEffect(() => {
    const fetchNotificationSettings = async () => {
      if (!user) return;
      
      try {
        const token = localStorage.getItem('access_token');
        const response = await axios.get(`${BACKEND_URL}/api/reviewers/notification-settings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setNotificationSettings(response.data);
      } catch (error) {
        console.error('Failed to fetch notification settings:', error);
        // Keep defaults
      }
    };
    
    fetchNotificationSettings();
  }, [user]);

  // Handle subscription return from Stripe
  useEffect(() => {
    const subscriptionParam = searchParams.get('subscription');
    const upgradeParam = searchParams.get('upgrade');
    
    // Handle free trial upgrade request from email
    if (upgradeParam === 'free-trial') {
      const startFreeTrial = async () => {
        try {
          const token = localStorage.getItem('access_token');
          
          toast({
            title: "🎁 Starting Your Free Trial...",
            description: "Redirecting you to secure checkout.",
            duration: 3000
          });
          
          const response = await axios.post(
            `${BACKEND_URL}/api/monetization/select-pro-plan-free-trial`,
            {},
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
          
          if (response.data.success && response.data.checkout_url) {
            // Redirect to Stripe checkout
            window.location.href = response.data.checkout_url;
          } else if (response.data.error) {
            toast({
              title: "Free Trial Unavailable",
              description: response.data.error,
              variant: "destructive"
            });
          }
        } catch (err) {
          console.error('Error starting free trial:', err);
          toast({
            title: "Error",
            description: err.response?.data?.detail || "Could not start free trial. Please try again.",
            variant: "destructive"
          });
        }
        
        // Clear the URL parameter
        searchParams.delete('upgrade');
        setSearchParams(searchParams);
      };
      
      startFreeTrial();
      return;
    }
    
    if (subscriptionParam === 'success') {
      // Verify and activate subscription via backend (backup for webhook)
      const verifyAndActivateSubscription = async () => {
        try {
          const token = localStorage.getItem('access_token');
          
          // Call verify endpoint to sync subscription status from Stripe
          const verifyResponse = await axios.post(
            `${BACKEND_URL}/api/monetization/subscription/verify`,
            {},
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
          
          console.log('Subscription verification:', verifyResponse.data);
          
          if (verifyResponse.data.subscription_active) {
            toast({
              title: "🎉 Pro Subscription Activated!",
              description: "Welcome to the Mic Check Millionaire Pro Plan! You'll receive 100% of all tips.",
              duration: 5000
            });
          } else {
            // Subscription not found - might be processing delay
            toast({
              title: "⏳ Processing Subscription",
              description: "Your subscription is being processed. Please refresh in a moment.",
              duration: 5000
            });
          }
          
          // Refresh user data
          const userResponse = await axios.get(`${BACKEND_URL}/api/auth/me`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          setUser(userResponse.data);
          localStorage.setItem('user', JSON.stringify(userResponse.data));
          setMonetizationPlan(userResponse.data.monetization_plan);
          
        } catch (err) {
          console.error('Error verifying subscription:', err);
          toast({
            title: "Subscription Verification",
            description: "Please refresh the page to see your updated subscription status.",
            variant: "default"
          });
        }
      };
      
      verifyAndActivateSubscription();
      
      // Clear the URL parameter
      searchParams.delete('subscription');
      setSearchParams(searchParams);
      
    } else if (subscriptionParam === 'trial_success') {
      // Handle free trial success
      toast({
        title: "🎉 Free Trial Activated!",
        description: "Your 30-day Pro Plan trial has started. Enjoy keeping 100% of your earnings!",
        duration: 5000
      });
      
      // Refresh user data
      const refreshUser = async () => {
        try {
          const token = localStorage.getItem('access_token');
          const userResponse = await axios.get(`${BACKEND_URL}/api/auth/me`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          setUser(userResponse.data);
          localStorage.setItem('user', JSON.stringify(userResponse.data));
          setMonetizationPlan(userResponse.data.monetization_plan);
        } catch (err) {
          console.error('Error refreshing user:', err);
        }
      };
      refreshUser();
      
      searchParams.delete('subscription');
      setSearchParams(searchParams);
      
    } else if (subscriptionParam === 'cancelled') {
      // Track abandoned checkout and potentially send follow-up email
      const trackAbandonedCheckout = async () => {
        try {
          const token = localStorage.getItem('access_token');
          const response = await axios.post(
            `${BACKEND_URL}/api/monetization/pro-checkout-abandoned`,
            {},
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
          
          // Show enhanced toast based on response
          if (response.data.show_upgrade_banner) {
            toast({
              title: "👋 No Problem — You're on the Free Plan!",
              description: (
                <div className="space-y-2">
                  <p>You can start receiving tips right now with Stripe (10% platform fee).</p>
                  <p className="text-sm text-muted-foreground">
                    <strong>Pro tip:</strong> If you earn $100+/month, the Pro Plan ($10/mo, 0% fee) actually saves you money!
                  </p>
                  <p className="text-xs text-muted-foreground">Upgrade anytime from Settings → Subscription.</p>
                </div>
              ),
              duration: 10000
            });
            
            // Store flag to show upgrade banner
            localStorage.setItem('show_pro_upgrade_banner', 'true');
          } else {
            toast({
              title: "Welcome Back!",
              description: "You're all set on your current plan.",
              duration: 5000
            });
          }
        } catch (err) {
          console.error('Error tracking abandoned checkout:', err);
          // Fallback toast
          toast({
            title: "👋 No Problem — You're on the Free Plan!",
            description: "You can receive tips via Stripe. Upgrade to Pro anytime from Settings.",
            duration: 5000
          });
        }
      };
      
      trackAbandonedCheckout();
      searchParams.delete('subscription');
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams, toast]);

  // Check if monetization plan needs to be chosen
  useEffect(() => {
    if (user && !loading) {
      const plan = user.monetization_plan;
      setMonetizationPlan(plan);
      
      // Show modal if no plan is set
      if (!plan) {
        setShowMonetizationModal(true);
      }
    }
  }, [user, loading]);

  // Unified session resolver - handles corrupted references gracefully
  // eslint-disable-next-line no-unused-vars
  const resolveSession = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return null;

      // Check for active session
      const activeResponse = await axios.get(
        `${BACKEND_URL}/api/sessions/active`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      ).catch(() => ({ data: { active: false } }));

      if (activeResponse.data.active && activeResponse.data.sessionId) {
        // Verify the session actually exists
        try {
          await axios.get(
            `${BACKEND_URL}/api/sessions/${activeResponse.data.sessionId}`,
            {
              headers: { 'Authorization': `Bearer ${token}` }
            }
          );
          
          // Session is valid
          return activeResponse.data.sessionId;
        } catch (verifyError) {
          // Session reference is corrupted - clean it up
          console.warn('⚠️ Active session reference corrupted, cleaning up...');
          
          await axios.delete(
            `${BACKEND_URL}/api/sessions/debug/cleanup`,
            {
              headers: { 'Authorization': `Bearer ${token}` }
            }
          ).catch(() => {});
          
          // Fall through to create new session
        }
      }

      return null;
    } catch (error) {
      console.error('Error resolving session:', error);
      return null;
    }
  };

  // Check for stuck session redirect state on mount
  // DISABLED: Don&apos;t auto-redirect to active session on dashboard load
  // Users can manually navigate to their active session if needed
  useEffect(() => {
    const checkStuckState = async () => {
      // DISABLED: Allow reviewers to stay on dashboard even with active session
      // if (window.location.pathname === '/reviewer-dashboard') {
      //   const sessionId = await resolveSession();
      //   if (sessionId) {
      //     console.log('✅ Found active session, auto-routing to:', sessionId);
      //     navigate(`/session/${sessionId}`);
      //   }
      // }
    };

    if (user && isAuthed) {
      checkStuckState();
    }
  }, [user, isAuthed, navigate]);

  // Check Spotify connection status on mount
  useEffect(() => {
    if (user && isAuthed) {
      checkSpotifyStatus();
    }
  }, [user, isAuthed]);

  // Check for active session
  useEffect(() => {
    const checkActiveSession = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        
        if (!token || !storedUser.id) return;
        
        const response = await axios.get(
          `${BACKEND_URL}/api/sessions/reviewer/${storedUser.id}/active`,
          {
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );
        
        // New response format: {active: boolean, session: object|null}
        if (response.data?.active && response.data?.session) {
          setActiveSession(response.data.session);
        } else if (response.data?.is_active) {
          // Legacy format fallback
          setActiveSession(response.data);
        } else {
          // No active session - this is normal, not an error
          setActiveSession(null);
        }
      } catch (error) {
        // Handle errors gracefully
        if (error.response?.status >= 500) {
          // Server error - log but don&apos;t break UI
          console.warn('Session service temporarily unavailable');
        }
        // For any error, treat as "no active session" - don&apos;t break UI
        setActiveSession(null);
      }
    };
    
    checkActiveSession();
  }, [user]);

  // Fetch session history with pagination
  useEffect(() => {
    const fetchSessionHistory = async (page = 1, append = false) => {
      try {
        const token = localStorage.getItem('access_token');
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        
        if (!token || !storedUser.id) return;
        
        const response = await axios.get(
          `${BACKEND_URL}/api/sessions/reviewer/${storedUser.id}/sessions`,
          {
            headers: { 'Authorization': `Bearer ${token}` },
            params: {
              page: page,
              page_size: 50
            }
          }
        );
        
        if (response.data) {
          const { sessions: newSessions, total, page: currentPage, has_more } = response.data;
          
          if (append) {
            // Append to existing sessions (for "Load More")
            setSessions(prev => [...prev, ...newSessions]);
          } else {
            // Replace sessions (for initial load or refresh)
            setSessions(newSessions);
          }
          
          setSessionTotal(total);
          setSessionPage(currentPage);
          setSessionHasMore(has_more);
        }
      } catch (error) {
        console.error('Error fetching session history:', error);
      }
    };
    
    if (user && user.id) {
      // Initial fetch (page 1)
      fetchSessionHistory(1, false);
      
      // Set up real-time polling (every 30 seconds) - only refresh first page
      const pollInterval = setInterval(() => {
        fetchSessionHistory(1, false);
      }, 30000); // 30 seconds
      
      // Cleanup interval on unmount
      return () => clearInterval(pollInterval);
    }
  }, [user]);

  // Fetch sessions function (can be called anytime to refresh)
  const fetchSessions = async (page = 1, append = false) => {
    try {
      const token = localStorage.getItem('access_token');
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      
      if (!token || !storedUser.id) return;
      
      const response = await axios.get(
        `${BACKEND_URL}/api/sessions/reviewer/${storedUser.id}/sessions`,
        {
          headers: { 'Authorization': `Bearer ${token}` },
          params: {
            page: page,
            page_size: 50
          }
        }
      );
      
      if (response.data) {
        const { sessions: newSessions, total, page: currentPage, has_more } = response.data;
        
        if (append) {
          setSessions(prev => [...prev, ...newSessions]);
        } else {
          setSessions(newSessions);
        }
        
        setSessionTotal(total);
        setSessionPage(currentPage);
        setSessionHasMore(has_more);
      }
    } catch (error) {
      console.error('Error fetching sessions:', error);
    }
  };
  
  // Load more sessions (pagination)
  const loadMoreSessions = async () => {
    if (loadingMoreSessions || !sessionHasMore) return;
    
    setLoadingMoreSessions(true);
    try {
      await fetchSessions(sessionPage + 1, true);
    } finally {
      setLoadingMoreSessions(false);
    }
  };

  // Fetch collected artist emails (for PRO Emails tab)
  const fetchCollectedEmails = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      setEmailsLoading(false);
      return;
    }
    
    setEmailsLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/reviewer/collected-emails`, {
        headers: { 'Authorization': `Bearer ${token}` },
        timeout: 10000 // 10 second timeout
      });
      setCollectedEmails(response.data.emails || []);
      setEmailsCanExport(response.data.can_export || false);
      
      // Also fetch stats
      const statsResponse = await axios.get(`${BACKEND_URL}/api/reviewer/collected-emails/stats`, {
        headers: { 'Authorization': `Bearer ${token}` },
        timeout: 10000
      });
      setEmailsStats(statsResponse.data);
    } catch (error) {
      console.error('Error fetching collected emails:', error);
      // On error, show empty state instead of infinite loading
      setCollectedEmails([]);
    } finally {
      setEmailsLoading(false);
    }
  }, []);
  
  // Export emails to CSV (PRO feature)
  const handleExportEmails = async () => {
    const token = localStorage.getItem('access_token');
    if (!token) return;
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/reviewer/collected-emails/export`, {
        headers: { 'Authorization': `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `artist_emails_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast({
        title: "Export Complete",
        description: "Your artist emails have been downloaded.",
      });
    } catch (error) {
      if (error.response?.status === 403) {
        toast({
          title: "PRO Feature",
          description: "Upgrade to PRO to export your artist emails.",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Export Failed",
          description: "Could not export emails. Please try again.",
          variant: "destructive"
        });
      }
    }
  };
  
  // Fetch emails when tab changes to 'emails'
  useEffect(() => {
    if (activeTab === 'emails' && collectedEmails.length === 0 && !emailsLoading) {
      fetchCollectedEmails();
    }
  }, [activeTab, collectedEmails.length, emailsLoading, fetchCollectedEmails]);

  const handleSaveBio = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      // Only send username/email if they changed
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      const profileUpdate = {
        name: displayName,
        bio,
        genres: Array.isArray(genres) ? genres : genres.split(',').map(g => g.trim()),
        card_image_position: cardImagePosition
      };
      
      // Only include username if it's different from current
      if (username !== storedUser.username) {
        profileUpdate.username = username;
      }
      
      // Only include email if it's different from current
      if (email !== storedUser.email) {
        profileUpdate.email = email;
      }
      
      await axios.put(
        `${BACKEND_URL}/api/auth/update-profile`,
        profileUpdate,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      // Update localStorage
      storedUser.name = displayName;
      storedUser.bio = bio;
      storedUser.card_image_position = cardImagePosition;
      if (username !== storedUser.username) {
        storedUser.username = username;
      }
      if (email !== storedUser.email) {
        storedUser.email = email;
      }
      storedUser.genres = Array.isArray(genres) ? genres : genres.split(',').map(g => g.trim());
      localStorage.setItem('user', JSON.stringify(storedUser));
      setUser(storedUser);

      toast({
        title: "✅ Profile updated!",
        description: "Your profile has been saved successfully."
      });
    } catch (error) {
      console.error('Profile update error:', error);
      toast({
        title: "❌ Error",
        description: error.response?.data?.detail || "Failed to update profile.",
        variant: "destructive"
      });
    }
  };

  const handleSaveSettings = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      // Map frontend settings to backend format
      const submissionSettings = {
        skip_line_enabled: settings.skipLineEnabled,
        tips_enabled: settings.tipsEnabled,
        allow_spotify: settings.allowSpotify,
        allow_soundcloud: settings.allowSoundcloud,
        allow_youtube: settings.allowYoutube,
        allow_file_uploads: settings.allowFileUploads,
        require_email: settings.requireEmail,
        require_spotify_follow: settings.requireSpotifyFollow,
        twitch_sub_skip: settings.twitchSubSkip
      };
      
      await axios.put(
        `${BACKEND_URL}/api/reviewers/submission-settings`,
        submissionSettings,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast({
        title: "Settings saved",
        description: "Your preferences have been updated."
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: "Error",
        description: "Failed to save settings.",
        variant: "destructive"
      });
    }
  };

  const handleDeleteAccount = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      toast({
        title: "Deleting account...",
        description: "Please wait while we process your request."
      });
      
      const response = await axios.delete(
        `${BACKEND_URL}/api/auth/delete-account`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast({
          title: "Account deleted",
          description: "Your account has been permanently deleted. You will be redirected to the homepage."
        });
        
        // Clear all local storage
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
        
        // Redirect to homepage after 2 seconds
        setTimeout(() => {
          navigate('/');
        }, 2000);
      }
    } catch (error) {
      console.error('Error deleting account:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to delete account. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleRenameSession = async (sessionId, newName) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.patch(
        `${BACKEND_URL}/api/sessions/${sessionId}/rename?session_name=${encodeURIComponent(newName)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setSessions(sessions.map(s => 
        s.session_id === sessionId ? { ...s, session_name: newName } : s
      ));
      
      setEditingSessionId(null);
      setEditingSessionName('');
      
      toast({
        title: "Session renamed",
        description: "Your session name has been updated successfully."
      });
    } catch (error) {
      console.error('Error renaming session:', error);
      toast({
        title: "Error",
        description: "Failed to rename session. Please try again.",
        variant: "destructive"
      });
    }
  };


  const handleToggleAvailability = async () => {
    try {
      const newIsLive = !settings.isAvailable;
      const token = localStorage.getItem('access_token');
      
      console.log('Toggling availability to:', newIsLive);
      
      // Update live status on backend
      const response = await axios.patch(
        `${BACKEND_URL}/api/reviewers/${user.id}/live-status`,
        { is_live: newIsLive },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('Live status response:', response.data);
      
      // Update local state
      setSettings({ ...settings, isAvailable: newIsLive });
      setUser({ ...user, is_live: newIsLive });
      
      // Update localStorage
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      storedUser.is_live = newIsLive;
      localStorage.setItem('user', JSON.stringify(storedUser));
      
      // If going offline, end any active session
      if (!newIsLive) {
        try {
          const activeSessionResponse = await axios.get(
            `${BACKEND_URL}/api/sessions/reviewer/${user.id}/active`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          // Handle new response format: {active: boolean, session: object|null}
          const data = activeSessionResponse.data;
          const sessionToEnd = data?.session || (data?.session_id ? data : null);
          
          if (sessionToEnd && sessionToEnd.session_id) {
            await axios.post(
              `${BACKEND_URL}/api/sessions/${sessionToEnd.session_id}/end`,
              {},
              { headers: { Authorization: `Bearer ${token}` } }
            );
            setActiveSession(null);
          }
        } catch (err) {
          // No active session to end - this is normal
        }
      } else {
        // Going online - check if there&apos;s a resumable session
        fetchSessions();
      }
      
      toast({
        title: newIsLive ? "You&apos;re now live! 🎵" : "You&apos;re now offline",
        description: newIsLive 
          ? "You can now start a new session or resume your last session." 
          : "Your session has ended. See you next time!"
      });
    } catch (error) {
      console.error('Error toggling availability:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('User ID used:', user?.id);
      
      let errorMsg = "Failed to update live status. Please try again.";
      if (error.response?.status === 401) {
        errorMsg = "Session expired. Please log out and log back in.";
      } else if (error.response?.status === 403) {
        errorMsg = "Authorization error. Please log out and log back in.";
      } else if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      } else if (!error.response) {
        errorMsg = "Network error. Please check your connection and try again.";
      }
      
      toast({
        title: "Error",
        description: errorMsg,
        variant: "destructive"
      });
    }
  };

  // Resume last session
  const handleResumeSession = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      if (!token) {
        toast({
          title: "Authentication required",
          description: "Please log in first.",
          variant: "destructive"
        });
        return;
      }

      toast({
        title: "Checking for active session...",
        description: "Please wait..."
      });

      // Check for active session (consolidated route)
      const response = await axios.get(
        `${BACKEND_URL}/api/sessions/active`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.data.active && response.data.sessionId) {
        toast({
          title: "Session Found",
          description: "Resuming your session..."
        });
        
        navigate(`/session/${response.data.sessionId}`);
      } else {
        toast({
          title: "No Active Session",
          description: "You don&apos;t have any sessions to resume. Start a new one instead.",
          variant: "default"
        });
      }
    } catch (error) {
      console.error('Error resuming session:', error);
      
      toast({
        title: "No Active Session",
        description: "Start a new session to begin reviewing.",
        variant: "default"
      });
    }
  };

  // Check Spotify connection status
  const checkSpotifyStatus = async () => {
    try {
      setCheckingSpotifyStatus(true);
      const token = localStorage.getItem('access_token');
      
      if (!token) {
        setSpotifyConnected(false);
        return;
      }

      const response = await axios.get(
        `${BACKEND_URL}/api/oauth/spotify/status`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      // Status endpoint now validates token freshness and attempts refresh
      setSpotifyConnected(response.data.connected);
      
      // If needs_refresh is true, show reconnect message
      if (response.data.needs_refresh) {
        console.warn('Spotify token needs refresh - connection may be stale');
      }
    } catch (error) {
      console.error('Error checking Spotify status:', error);
      // Default to not connected on error
      setSpotifyConnected(false);
    } finally {
      setCheckingSpotifyStatus(false);
    }
  };

  // Spotify OAuth Connection with Popup
  const handleConnectSpotify = () => {
    const userId = user?.id;
    if (!userId) {
      toast({
        title: "Error",
        description: "User ID not found. Please refresh the page.",
        variant: "destructive"
      });
      return;
    }

    // Get current origin for dynamic redirect URI
    const currentOrigin = window.location.origin;
    const authUrl = `${BACKEND_URL}/api/oauth/spotify/authorize?user_id=${userId}&origin=${encodeURIComponent(currentOrigin)}`;
    
    // Open in popup window
    const popup = window.open(
      authUrl,
      'Spotify Authorization',
      'width=600,height=700,scrollbars=yes'
    );

    // Listen for popup to close
    const checkPopup = setInterval(() => {
      if (popup && popup.closed) {
        clearInterval(checkPopup);
        
        // Show loading toast
        toast({
          title: "Checking connection...",
          description: "Please wait while we verify your Spotify connection."
        });
        
        // Wait a bit for backend to process, then refresh user data
        setTimeout(async () => {
          const updatedUser = await fetchUserData();
          
          // Refresh Spotify connection status
          await checkSpotifyStatus();
          
          // Check if spotify_token exists after refresh
          if (updatedUser && updatedUser.spotify_token) {
            toast({
              title: "Spotify Connected! 🎉",
              description: "You can now play Spotify tracks in your sessions."
            });
          } else {
            toast({
              title: "Connection Status",
              description: "Please check if Spotify authorization completed successfully.",
              variant: "default"
            });
          }
        }, 1500);
      }
    }, 500);
  };

  // Debug: Force end all active sessions
  // eslint-disable-next-line no-unused-vars
  const handleForceEndSessions = async () => {
    if (!window.confirm('Force end all active sessions? This is a debug tool.')) {
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${BACKEND_URL}/api/sessions/debug/force-end-all`,
        {},
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      toast({
        title: "Debug: Sessions Ended",
        description: response.data.message,
      });

      // Refresh sessions list
      await fetchSessions();
    } catch (error) {
      console.error('Error force-ending sessions:', error);
      
      if (error.response?.status === 404) {
        toast({
          title: "Debug Tools Disabled",
          description: "Debug tools are not enabled on this instance.",
          variant: "default"
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to force-end sessions.",
          variant: "destructive"
        });
      }
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleDisconnectSpotify = async () => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${BACKEND_URL}/api/oauth/spotify/disconnect`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local user state
      setUser({ ...user, spotify_token: null });
      
      // Refresh connection status
      await checkSpotifyStatus();
      
      toast({
        title: "Spotify Disconnected",
        description: "Your Spotify account has been disconnected."
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to disconnect Spotify.",
        variant: "destructive"
      });
    }
  };

  // SoundCloud OAuth handlers removed - no longer needed

  // Helper function to refresh user data
  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const freshUser = response.data;
      localStorage.setItem('user', JSON.stringify(freshUser));
      setUser(freshUser);
      
      return freshUser; // Return the fresh user data
    } catch (error) {
      console.error('Failed to refresh user data:', error);
      return null;
    }
  };

  const handleAvatarSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid file",
        description: "Please upload an image file.",
        variant: "destructive"
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Please upload an image smaller than 5MB.",
        variant: "destructive"
      });
      return;
    }

    // Reset input so same file can be selected again
    e.target.value = '';
    
    // Read file and open crop modal
    const reader = new FileReader();
    reader.onload = () => {
      setSelectedImageForCrop(reader.result);
      setShowCropModal(true);
    };
    reader.readAsDataURL(file);
  };
  
  const handleCroppedImageSave = async (croppedBlob) => {
    // Convert blob to file and upload
    const file = new File([croppedBlob], 'avatar.jpg', { type: 'image/jpeg' });
    await handleAvatarUpload(file);
  };

  const handleAvatarUpload = async (file) => {
    try {
      setUploadingAvatar(true);
      console.log('🎨 Starting avatar upload...', file.name);
      
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('access_token');
      
      if (!token) {
        toast({
          title: "❌ Not authenticated",
          description: "Please log in again.",
          variant: "destructive"
        });
        return;
      }
      
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/upload-avatar`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      console.log('📤 Upload response:', response.data);

      // Fetch fresh user data from backend to get updated avatarUrl
      try {
        const freshUserResponse = await axios.get(`${BACKEND_URL}/api/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const freshUser = freshUserResponse.data;
        
        // Use SINGLE CANONICAL avatarUrl field
        const finalAvatarUrl = getAvatarUrl(freshUser);
        
        console.log('✅ Avatar uploaded successfully');
        console.log('avatarUrl from server:', freshUser.avatarUrl);
        console.log('avatar_updated_at:', freshUser.avatar_updated_at);
        console.log('Final avatar URL:', finalAvatarUrl);
        
        // Update localStorage with fresh user data
        localStorage.setItem('user', JSON.stringify(freshUser));
        setUser(freshUser);
        setAvatar(finalAvatarUrl || '');
        
        // CRITICAL: Force browser cache invalidation
        // Clear any cached images for this user
        if ('caches' in window) {
          caches.keys().then(names => {
            names.forEach(name => {
              caches.delete(name);
            });
          });
        }
        
        // Dispatch global event for immediate UI updates
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('avatar-updated', { 
            detail: { 
              userId: freshUser.id || freshUser._id,
              avatarUrl: finalAvatarUrl,
              timestamp: Date.now()
            }
          }));
          console.log('✅ Dispatched avatar-updated event - all components will refresh');
        }, 100);
        
        console.log('✅ Avatar update complete - visible everywhere');
      } catch (syncError) {
        console.error('❌ Could not sync with backend after avatar upload:', syncError);
        // Fallback
        const avatarUrl = response.data.avatarUrl || response.data.avatar_url;
        const fullAvatarUrl = avatarUrl.startsWith('http') ? avatarUrl : `${BACKEND_URL}${avatarUrl}`;
        setAvatar(`${fullAvatarUrl}?v=${Date.now()}&_=${Math.random()}`);
      }

      // Show success message
      toast({
        title: "Profile photo updated successfully",
        description: "Your new avatar is now live."
      });
      
    } catch (error) {
      console.error('❌ Avatar upload error:', error);
      console.error('Error details:', error.response?.data);
      
      const errorMsg = error.response?.data?.detail || error.message || "Failed to upload. Please try again.";
      
      toast({
        title: "❌ Upload failed",
        description: errorMsg,
        variant: "destructive"
      });
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleSendLiveNotification = async () => {
    try {
      setSendingNotification(true);
      const token = localStorage.getItem('access_token');
      
      const response = await axios.post(
        `${BACKEND_URL}/api/reviewers/notify-followers`,
        {},
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      // Build description based on what was sent
      const { sms_sent, email_sent, total_notified } = response.data;
      let description = '';
      if (sms_sent > 0 && email_sent > 0) {
        description = `Sent ${sms_sent} SMS and ${email_sent} email notifications!`;
      } else if (sms_sent > 0) {
        description = `Sent ${sms_sent} SMS notification(s) to your followers!`;
      } else if (email_sent > 0) {
        description = `Sent ${email_sent} email notification(s) to your followers!`;
      } else {
        description = 'No followers with valid contact info found.';
      }

      toast({
        title: total_notified > 0 ? "✅ Notifications sent!" : "ℹ️ No notifications sent",
        description: description
      });
      
    } catch (error) {
      console.error('❌ Notification error:', error);
      toast({
        title: "Failed to send notifications",
        description: error.response?.data?.detail || "Please try again.",
        variant: "destructive"
      });
    } finally {
      setSendingNotification(false);
    }
  };

  const handleUpdateNotificationSetting = async (key, value) => {
    try {
      const token = localStorage.getItem('access_token');
      
      // Optimistically update UI
      setNotificationSettings(prev => ({ ...prev, [key]: value }));
      
      await axios.put(
        `${BACKEND_URL}/api/reviewers/notification-settings`,
        { [key]: value },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      toast({
        title: "Settings updated",
        description: key === 'auto_notify_on_session_start' 
          ? (value ? "Auto-notify enabled" : "Auto-notify disabled")
          : (value ? "Queue count will be included" : "Queue count hidden"),
      });
      
    } catch (error) {
      console.error('Failed to update notification setting:', error);
      // Revert on error
      setNotificationSettings(prev => ({ ...prev, [key]: !value }));
      toast({
        title: "Failed to update setting",
        description: "Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleSaveCustomMessages = async () => {
    try {
      setSavingCustomMessage(true);
      const token = localStorage.getItem('access_token');
      
      await axios.put(
        `${BACKEND_URL}/api/reviewers/notification-settings`,
        {
          custom_sms_message: notificationSettings.custom_sms_message,
          custom_email_message: notificationSettings.custom_email_message,
          custom_email_subject: notificationSettings.custom_email_subject
        },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      toast({
        title: "✅ Custom messages saved",
        description: "Your personalized notification messages have been saved."
      });
      
    } catch (error) {
      console.error('Failed to save custom messages:', error);
      toast({
        title: "Failed to save messages",
        description: "Please try again.",
        variant: "destructive"
      });
    } finally {
      setSavingCustomMessage(false);
    }
  };

  // Handle scroll to incomplete profile field
  const handleScrollToIncompleteField = useCallback(() => {
    const missingFields = getMissingFields(user);
    
    if (missingFields.length === 0) return;
    
    // First field to complete - scroll to it
    const firstMissing = missingFields[0];
    
    // Click on the Bio tab first to ensure the content is visible
    if (bioTabRef.current) {
      bioTabRef.current.click();
    }
    
    // Give the tab time to activate, then scroll
    setTimeout(() => {
      let targetRef = null;
      
      switch (firstMissing) {
        case 'avatar':
          targetRef = avatarSectionRef;
          break;
        case 'displayName':
          targetRef = displayNameRef;
          break;
        case 'bio':
          targetRef = bioRef;
          break;
        default:
          targetRef = avatarSectionRef;
      }
      
      if (targetRef?.current) {
        targetRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
        
        // Add a brief highlight effect
        targetRef.current.classList.add('ring-2', 'ring-[#F2D857]', 'ring-offset-2');
        setTimeout(() => {
          targetRef.current?.classList.remove('ring-2', 'ring-[#F2D857]', 'ring-offset-2');
        }, 2000);
      }
    }, 100);
  }, [user]);

  const handleStartNewSession = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      if (!token) {
        toast({
          title: "Authentication required",
          description: "Please log in to start a session.",
          variant: "destructive"
        });
        return;
      }

      toast({
        title: "Starting session...",
        description: "Creating your session..."
      });

      // Use consolidated start-clean endpoint that ALWAYS succeeds
      const response = await axios.post(
        `${BACKEND_URL}/api/sessions/start-clean`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const sessionId = response.data.sessionId;
      
      // Refresh sessions list to update UI
      await fetchSessions();
      
      toast({
        title: "✅ New session created!",
        description: "Redirecting to your live session..."
      });

      // Redirect to the session dashboard
      setTimeout(() => {
        navigate(`/session/${sessionId}`);
      }, 500);

    } catch (error) {
      console.error('Error starting new session:', error);
      
      toast({
        title: "❌ Error",
        description: error.response?.data?.detail || "Failed to start session. Please try again.",
        variant: "destructive"
      });
    }
  };

  // One-click Start Session: go live + create session + redirect
  const handleOneClickStartSession = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      if (!token) {
        toast({
          title: "Authentication required",
          description: "Please log in to start a session.",
          variant: "destructive"
        });
        return;
      }

      // Step 1: Set live status (don't update local state — avoids button flash)
      await axios.patch(
        `${BACKEND_URL}/api/reviewers/${user.id}/live-status`,
        { is_live: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Step 2: Create session
      const response = await axios.post(
        `${BACKEND_URL}/api/sessions/start-clean`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const sessionId = response.data.sessionId;

      // Update localStorage for when they return to dashboard
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      storedUser.is_live = true;
      localStorage.setItem('user', JSON.stringify(storedUser));

      // Step 3: Redirect immediately — no local state update, no flash
      navigate(`/session/${sessionId}`);

    } catch (error) {
      console.error('Error in one-click start:', error);
      
      let errorMsg = "Failed to start session. Please try again.";
      if (error.response?.status === 401) {
        errorMsg = "Session expired. Please log out and log back in.";
      } else if (error.response?.status === 403) {
        errorMsg = "Authorization error. Please log out and log back in.";
      } else if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      } else if (!error.response) {
        errorMsg = "Network error. Please check your connection and try again.";
      }
      
      toast({
        title: "Error",
        description: errorMsg,
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">No user data found. Please log in again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-black text-white py-6 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row justify-between items-center gap-4">
            <div className="flex items-center space-x-4">
              <Avatar key={avatar} className="h-16 w-16 border-2 border-[#FFCC00]">
                <AvatarImage src={avatar} alt={user?.name} />
                <AvatarFallback className="bg-[#FFCC00] text-black font-bold">
                  {user?.name?.charAt(0) || 'R'}
                </AvatarFallback>
              </Avatar>
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold font-montserrat">Reviewer Dashboard</h1>
                <p className="text-gray-400 text-sm lg:text-base">Welcome back, {user?.name}!</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
              {/* Check if payment method is set up before allowing Go Live */}
              {(() => {
                const hasStripeSetup = user?.stripe_details_submitted === true;
                const isProPlan = user?.monetization_plan === 'subscription_cashapp_venmo';
                const isSubscriptionActive = user?.subscription_status === 'active' || user?.subscription_status === 'trialing';
                const hasCashAppOrVenmo = !!(user?.cash_app || user?.venmo);
                
                // Pro Plan users can go live with Stripe OR Cash App/Venmo
                // Free Plan users must have Stripe
                const hasPaymentMethod = hasStripeSetup || (isProPlan && isSubscriptionActive && hasCashAppOrVenmo);
                const canGoLive = isAuthed && hasPaymentMethod;
                
                // Determine appropriate message for disabled state
                const getDisabledMessage = () => {
                  if (!isAuthed) return "Please log in to continue";
                  if (isProPlan && isSubscriptionActive) {
                    return "Add Stripe, Cash App, or Venmo to start a session";
                  }
                  return "Connect Stripe first to start a session";
                };
                
                return (
                  <>
                    <Button
                      onClick={canGoLive ? (settings.isAvailable ? handleToggleAvailability : handleOneClickStartSession) : undefined}
                      disabled={!canGoLive}
                      title={!canGoLive ? getDisabledMessage() : ""}
                      data-testid="start-session-btn"
                      data-tour="start-session-btn"
                      className={`w-full sm:w-auto ${
                        !canGoLive 
                          ? "bg-gray-400 cursor-not-allowed" 
                          : settings.isAvailable 
                            ? "bg-red-500 hover:bg-red-600 text-white" 
                            : "bg-[#FFCC00] hover:bg-[#FFCC00]/90 text-black"
                      }`}
                    >
                      <Radio className="h-4 w-4 mr-2" />
                      {settings.isAvailable ? 'End Session' : 'Start Session'}
                      {!hasPaymentMethod && <Lock className="h-3 w-3 ml-1" />}
                    </Button>
                    
                    {/* Resume Session Button - only show if there are sessions and user is online */}
                    {settings.isAvailable && sessions && sessions.length > 0 && (
                <Button
                  variant="outline"
                  className="w-full sm:w-auto border-2 border-green-500 text-green-500 hover:bg-green-500 hover:text-white transition-all"
                  onClick={handleResumeSession}
                  disabled={!isAuthed}
                  title={!isAuthed ? "Please log in to continue" : ""}
                >
                  <Play className="h-4 w-4 mr-2" />
                  Resume Last Session
                </Button>
              )}
              
              {/* Start New Session Button - only show when user has an active session */}
              {settings.isAvailable && (
                <Button
                  variant="outline"
                  className="w-full sm:w-auto border-2 border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white transition-all"
                  onClick={handleStartNewSession}
                  disabled={!isAuthed}
                  title={!isAuthed ? "Please log in to continue" : "Start a fresh new session"}
                >
                  <Play className="h-4 w-4 mr-2" />
                  Start New Session
                </Button>
              )}
              
              {/* Share Session Link Button - shows when live */}
              {settings.isAvailable && user?.username && (
                <Button
                  variant="outline"
                  className="w-full sm:w-auto border-2 border-[#FFCC00] text-[#FFCC00] hover:bg-[#FFCC00] hover:text-black transition-all"
                  onClick={() => {
                    const sessionUrl = `${window.location.origin}/r/${user.username}`;
                    navigator.clipboard.writeText(sessionUrl);
                    toast({
                      title: "🔗 Session Link Copied!",
                      description: "Share this link with artists to receive submissions",
                      duration: 3000
                    });
                  }}
                >
                  <Link className="h-4 w-4 mr-2" />
                  Copy Session Link
                </Button>
              )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* Profile Completion Banner - Non-dismissable */}
      <ProfileCompletionBanner 
        user={user} 
        onCompleteClick={() => handleScrollToIncompleteField()}
      />

      {/* Pro Upgrade Banner - Shown after abandoned checkout */}
      {showProUpgradeBanner && monetizationPlan === 'free_stripe_10percent' && (
        <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border-l-4 border-[#F2D857]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center space-x-4">
                <div className="bg-[#F2D857] p-2 rounded-full">
                  <DollarSign className="h-5 w-5 text-black" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">
                    💡 You started the Pro upgrade — pick up where you left off?
                  </p>
                  <p className="text-sm text-gray-600">
                    Keep <strong>100% of your tips</strong> with Pro ($10/mo). At $100+/month in tips, Pro actually saves you money vs. the 10% fee.
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Button 
                  onClick={handleUpgradeFromBanner}
                  className="bg-[#F2D857] hover:bg-[#e6c94d] text-black font-semibold px-6"
                  data-testid="upgrade-pro-banner-btn"
                >
                  Upgrade to Pro — $10/mo
                </Button>
                <button 
                  onClick={dismissProUpgradeBanner}
                  className="text-gray-400 hover:text-gray-600 p-1"
                  aria-label="Dismiss"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Auth Guard Banner */}
      {!isAuthed && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  <strong>Authentication Required:</strong> Please{' '}
                  <button 
                    onClick={() => navigate('/login')}
                    className="font-medium underline hover:text-yellow-800"
                  >
                    log in again
                  </button>
                  {' '}to access reviewer features (Start Session, etc.)
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Message Center - Company Announcements */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <MessageCenter />
      </div>

      {/* Stats Overview */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8" data-tour="stats-overview">
          <Card className="p-6 border-2 border-gray-200 hover:border-[#FFCC00] transition-all">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Reviews</p>
                <p className="text-3xl font-bold">{user?.totalReviews || 0}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-lg">
                <Music className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </Card>

          <Card className="p-6 border-2 border-gray-200 hover:border-[#FFCC00] transition-all">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Status</p>
                <div className="flex items-center space-x-2">
                  <div className={`w-3 h-3 rounded-full ${settings.isAvailable ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></div>
                  <span className="text-xl font-bold">{settings.isAvailable ? 'Online' : 'Offline'}</span>
                </div>
              </div>
              <div className="bg-purple-100 p-3 rounded-lg">
                <Radio className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </Card>

          <Card className="p-6 border-2 border-gray-200 hover:border-[#FFCC00] transition-all">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Avg Rating</p>
                <p className="text-3xl font-bold">{user?.average_rating || '5.0'}</p>
              </div>
              <div className="bg-yellow-100 p-3 rounded-lg">
                <Star className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
          </Card>

          <Card className="p-6 border-2 border-gray-200 hover:border-[#FFCC00] transition-all">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Earnings</p>
                <p className="text-3xl font-bold">${user?.total_earnings || 0}</p>
              </div>
              <div className="bg-green-100 p-3 rounded-lg">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </Card>
        </div>

        {/* Setup Checklist - Shows progress for all 5 requirements */}
        {!isProfileComplete(user) && (
          <div data-tour="setup-checklist">
          <SetupChecklistCard
            user={user}
            onConnectStripe={handleConnectStripeFromSetup}
            onGoLive={handleOneClickStartSession}
            onEditProfile={() => handleScrollToIncompleteField()}
            onDismiss={null}  // No dismiss for non-complete users
            isGrandfathered={false}
            variant="prominent"
          />
          </div>
        )}

        {/* Consolidated Visibility Boost + Profile Preview Card */}
        <VisibilityAndPreviewCard 
          user={user}
          onGoLive={handleOneClickStartSession}
          avatar={avatar}
          displayName={displayName}
          username={username}
          bio={bio}
          cardImagePosition={cardImagePosition}
          setCardImagePosition={setCardImagePosition}
        />

        {/* Main Content - Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          {/* Responsive Tab Navigation */}
          <div className="mb-6 reviewer-dashboard-tabs overflow-x-auto scrollbar-thin" role="region" aria-label="Reviewer dashboard sections">
            <TabsList className="flex flex-nowrap min-w-max bg-gray-100/50 p-1 rounded-lg gap-1" role="tablist">
              <TabsTrigger 
                ref={bioTabRef}
                value="bio"
                id="tab-bio"
                data-tour="tab-bio"
                aria-controls="panel-bio"
                className="data-[state=active]:bg-[#FFCC00] data-[state=active]:text-black min-h-[48px] px-4 lg:px-6 flex-shrink-0 text-sm lg:text-base font-medium transition-smooth hover:bg-gray-100 data-[state=active]:shadow-md rounded-md"
              >
                <User className="h-4 w-4 lg:h-5 lg:w-5 mr-2" />
                Bio
              </TabsTrigger>
              <TabsTrigger 
                value="sessions"
                id="tab-sessions"
                data-tour="tab-sessions"
                aria-controls="panel-sessions"
                className="data-[state=active]:bg-[#FFCC00] data-[state=active]:text-black min-h-[48px] px-4 lg:px-6 flex-shrink-0 text-sm lg:text-base font-medium transition-smooth hover:bg-gray-100 data-[state=active]:shadow-md rounded-md"
              >
                <Calendar className="h-4 w-4 lg:h-5 lg:w-5 mr-2" />
                Sessions
              </TabsTrigger>
              <TabsTrigger 
                value="emails"
                id="tab-emails"
                data-tour="tab-emails"
                aria-controls="panel-emails"
                data-testid="emails-tab"
                className="data-[state=active]:bg-[#FFCC00] data-[state=active]:text-black min-h-[48px] px-4 lg:px-6 flex-shrink-0 text-sm lg:text-base font-medium transition-smooth hover:bg-gray-100 data-[state=active]:shadow-md rounded-md"
              >
                <Mail className="h-4 w-4 lg:h-5 lg:w-5 mr-2" />
                Emails
              </TabsTrigger>
              <TabsTrigger 
                value="notifications"
                id="tab-notifications"
                data-tour="tab-notifications"
                aria-controls="panel-notifications"
                className="data-[state=active]:bg-[#FFCC00] data-[state=active]:text-black min-h-[48px] px-4 lg:px-6 flex-shrink-0 text-sm lg:text-base font-medium transition-smooth hover:bg-gray-100 data-[state=active]:shadow-md rounded-md"
              >
                <Bell className="h-4 w-4 lg:h-5 lg:w-5 mr-2" />
                Notify
              </TabsTrigger>
              <TabsTrigger 
                value="settings"
                id="tab-settings"
                data-tour="tab-settings"
                aria-controls="panel-settings"
                className="data-[state=active]:bg-[#FFCC00] data-[state=active]:text-black min-h-[48px] px-4 lg:px-6 flex-shrink-0 text-sm lg:text-base font-medium transition-smooth hover:bg-gray-100 data-[state=active]:shadow-md rounded-md"
              >
                <Settings className="h-4 w-4 lg:h-5 lg:w-5 mr-2" />
                Settings
              </TabsTrigger>
              <TabsTrigger 
                value="connect-spotify"
                id="tab-connect-spotify"
                data-tour="tab-spotify"
                aria-controls="panel-connect-spotify"
                data-testid="spotify-tab"
                className="data-[state=active]:bg-[#FFCC00] data-[state=active]:text-black min-h-[48px] px-4 lg:px-6 flex-shrink-0 text-sm lg:text-base font-medium transition-smooth hover:bg-gray-100 data-[state=active]:shadow-md rounded-md !visible !opacity-100"
                style={{ display: 'inline-flex' }}
              >
                <Music className="h-4 w-4 lg:h-5 lg:w-5 mr-2" />
                Spotify
                {spotifyConnected && (
                  <span className="ml-2 px-1.5 py-0.5 text-[10px] font-bold bg-green-500 text-white rounded-full leading-none">
                    ✓
                  </span>
                )}
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Bio Tab */}
          <TabsContent value="bio" id="panel-bio" data-tour="panel-bio" role="tabpanel" aria-labelledby="tab-bio" className="tab-content-animate">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Profile Information Card */}
              <Card className="p-4 sm:p-6 border-2 border-gray-200 hover:border-[#FFCC00] transition-smooth hover-lift">
                <h3 className="text-lg sm:text-xl font-bold font-montserrat mb-4">Profile Information</h3>
                
                <div className="space-y-6">
                  {/* Profile Picture Section */}
                  <div ref={avatarSectionRef} data-testid="avatar-section">
                    <Label className="text-sm font-medium">Profile Picture</Label>
                    <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 mt-3">
                      <Avatar key={avatar} className="h-24 w-24 sm:h-20 sm:w-20 border-2 border-gray-200 overflow-hidden">
                        <AvatarImage 
                          src={avatar} 
                          alt={user?.name} 
                          className="object-cover object-center w-full h-full"
                        />
                        <AvatarFallback className="bg-[#FFCC00] text-black font-bold text-2xl">
                          {user?.name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 w-full">
                        <input
                          type="file"
                          id="avatar-upload"
                          accept="image/jpeg,image/png,image/webp"
                          className="hidden"
                          onChange={handleAvatarSelect}
                          disabled={uploadingAvatar}
                        />
                        <Button 
                          variant="outline" 
                          className="border-2 w-full sm:w-auto min-h-[48px]"
                          onClick={() => document.getElementById('avatar-upload').click()}
                          type="button"
                          disabled={uploadingAvatar}
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          {uploadingAvatar ? 'Uploading...' : 'Upload New Photo'}
                        </Button>
                        <p className="text-xs text-gray-500 mt-2">JPG, PNG or WEBP (max 15MB, min 400×400)</p>
                      </div>
                    </div>
                  </div>

                  {/* Display Name */}
                  <div ref={displayNameRef} data-testid="display-name-section">
                    <Label className="text-sm font-medium">Display Name <span className="text-red-500">*</span></Label>
                    <Input
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      className="mt-2 h-12 text-base"
                      placeholder="Your display name"
                      data-testid="display-name-input"
                    />
                  </div>

                  <div>
                    <Label>Username</Label>
                    <Input
                      value={username}
                      onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                      className="mt-1 h-12"
                      placeholder="your_username"
                    />
                    <p className="text-xs text-gray-500 mt-1">Lowercase letters, numbers, and underscores only</p>
                  </div>

                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value.toLowerCase().trim())}
                      placeholder="your.email@example.com"
                      className="mt-1 h-12"
                    />
                    <p className="text-xs text-gray-500 mt-1">Used for account notifications and login</p>
                  </div>

                  <div ref={bioRef} data-testid="bio-section">
                    <Label>Bio <span className="text-red-500">*</span></Label>
                    <Textarea
                      value={bio}
                      onChange={(e) => {
                        // Enforce max length
                        if (e.target.value.length <= MAX_BIO_LENGTH) {
                          setBio(e.target.value);
                        }
                      }}
                      placeholder="Tell artists about your review style and expertise..."
                      className={`mt-1 min-h-[120px] ${bio.length < MIN_BIO_LENGTH ? 'border-amber-400 focus:border-amber-500' : ''}`}
                      data-testid="bio-textarea"
                    />
                    <div className="flex justify-between items-center mt-1">
                      <p className={`text-xs ${bio.length < MIN_BIO_LENGTH ? 'text-amber-600' : 'text-gray-500'}`}>
                        {bio.length < MIN_BIO_LENGTH 
                          ? `${MIN_BIO_LENGTH - bio.length} more characters needed` 
                          : 'Looks great!'
                        }
                      </p>
                      <p className={`text-xs ${bio.length > MAX_BIO_LENGTH * 0.9 ? 'text-amber-600' : 'text-gray-500'}`}>
                        {bio.length}/{MAX_BIO_LENGTH}
                      </p>
                    </div>
                  </div>

                  <div>
                    <Label>Genre Specialties</Label>
                    <div className="mt-1 border rounded-md p-2 min-h-[48px] flex flex-wrap gap-2 items-center bg-white focus-within:ring-2 focus-within:ring-[#FFCC00] focus-within:border-[#FFCC00]">
                      {genres.map((genre, index) => (
                        genre && (
                          <span 
                            key={index} 
                            className="inline-flex items-center gap-1 bg-[#FFCC00]/20 text-black px-2 py-1 rounded-md text-sm"
                          >
                            {genre}
                            <button
                              type="button"
                              onClick={() => setGenres(genres.filter((_, i) => i !== index))}
                              className="hover:bg-black/10 rounded-full p-0.5 transition-colors"
                            >
                              <span className="text-xs">✕</span>
                            </button>
                          </span>
                        )
                      ))}
                      <input
                        type="text"
                        placeholder={genres.length === 0 ? "Type genre and press Enter or comma" : "Add more..."}
                        className="flex-1 min-w-[120px] outline-none text-sm py-1"
                        onKeyDown={(e) => {
                          const value = e.target.value.trim();
                          if ((e.key === 'Enter' || e.key === ',') && value) {
                            e.preventDefault();
                            if (!genres.includes(value)) {
                              setGenres([...genres, value]);
                            }
                            e.target.value = '';
                          } else if (e.key === 'Backspace' && !e.target.value && genres.length > 0) {
                            setGenres(genres.slice(0, -1));
                          }
                        }}
                        onBlur={(e) => {
                          const value = e.target.value.trim();
                          if (value && !genres.includes(value)) {
                            setGenres([...genres, value]);
                            e.target.value = '';
                          }
                        }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Press Enter or comma to add a genre</p>
                  </div>

                  <Button 
                    onClick={handleSaveBio}
                    className="w-full bg-[#FFCC00] hover:bg-[#FFCC00]/90 text-black font-semibold h-12"
                  >
                    Save Profile
                  </Button>
                </div>
              </Card>

              {/* Social Integrations Card */}
              <Card className="p-4 sm:p-6 border-2 border-gray-200 hover:border-[#FFCC00] transition-all" data-tour="social-integrations">
                <h3 className="text-lg sm:text-xl font-bold font-montserrat mb-4">Social Integrations</h3>
                
                <div className="space-y-6">
                  {/* MONETIZATION GATING: Cash App - ONLY FOR PAID PLAN */}
                  {/* Read-only display - editing happens in Settings → Direct */}
                  {user?.monetization_plan === 'subscription_cashapp_venmo' && monetizationPlan === 'subscription_cashapp_venmo' && (
                    <div>
                      <Label className="text-sm font-medium flex items-center gap-2">
                        <span className="text-lg">💵</span>
                        Cash App Handle
                      </Label>
                      <div className="relative">
                        <Input
                          value={user?.cash_app ? (user.cash_app.includes('cash.app') ? user.cash_app.split('/').pop().replace('$', '') : user.cash_app.replace('$', '')) : ''}
                          disabled
                          placeholder="Not set up yet"
                          className="mt-2 h-12 text-base bg-gray-100 text-gray-600 cursor-not-allowed"
                        />
                        {user?.cash_app && (
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 mt-1 text-green-600">
                            <Check className="h-5 w-5" />
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        {user?.cash_app ? (
                          <>✓ Connected — <button onClick={() => setActiveTab('settings')} className="text-blue-600 hover:underline">Edit in Settings</button></>
                        ) : (
                          <>Set up in <button onClick={() => setActiveTab('settings')} className="text-blue-600 hover:underline">Settings → Direct Payment</button></>
                        )}
                      </p>
                    </div>
                  )}
                  
                  {/* MONETIZATION GATING: Venmo - ONLY FOR PAID PLAN */}
                  {/* Read-only display - editing happens in Settings → Direct */}
                  {user?.monetization_plan === 'subscription_cashapp_venmo' && monetizationPlan === 'subscription_cashapp_venmo' && (
                    <div>
                      <Label className="text-sm font-medium flex items-center gap-2">
                        <span className="text-lg">💙</span>
                        Venmo Handle
                      </Label>
                      <div className="relative">
                        <Input
                          value={user?.venmo ? (user.venmo.includes('venmo.com') ? user.venmo.split('/').pop().replace('@', '') : user.venmo.replace('@', '')) : ''}
                          disabled
                          placeholder="Not set up yet"
                          className="mt-2 h-12 text-base bg-gray-100 text-gray-600 cursor-not-allowed"
                        />
                        {user?.venmo && (
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 mt-1 text-blue-600">
                            <Check className="h-5 w-5" />
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        {user?.venmo ? (
                          <>✓ Connected — <button onClick={() => setActiveTab('settings')} className="text-blue-600 hover:underline">Edit in Settings</button></>
                        ) : (
                          <>Set up in <button onClick={() => setActiveTab('settings')} className="text-blue-600 hover:underline">Settings → Direct Payment</button></>
                        )}
                      </p>
                    </div>
                  )}
                  
                  {/* Upgrade CTA for Free Plan Users */}
                  {user?.monetization_plan === 'free_stripe_10percent' && (
                    <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-400 rounded-lg p-5">
                      <div className="flex items-start">
                        <DollarSign className="h-6 w-6 text-yellow-700 mr-3 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="font-bold text-yellow-900 mb-2">🔒 Unlock Direct Payments</p>
                          <p className="text-sm text-yellow-800 mb-3">
                            Upgrade to the <strong>$10/month Pro Plan</strong> to add your Cash App and keep 100% of your tips!
                          </p>
                          <Button 
                            size="sm"
                            variant="outline" 
                            className="border-2 border-yellow-600 text-yellow-900 hover:bg-yellow-100 font-semibold"
                            onClick={() => setShowMonetizationModal(true)}
                          >
                            View Plans & Upgrade
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Twitch */}
                  <div>
                    <Label className="text-sm font-medium flex items-center gap-2">
                      <span className="text-lg">🎮</span>
                      Twitch Username
                    </Label>
                    <Input
                      value={socialLinks.twitch}
                      onChange={(e) => setSocialLinks({...socialLinks, twitch: e.target.value})}
                      placeholder="https://twitch.tv/your_channel"
                      className="mt-2 h-12 text-base"
                    />
                    <p className="text-xs text-gray-500 mt-2">Full Twitch channel URL</p>
                  </div>

                  {/* Instagram */}
                  <div>
                    <Label className="text-sm font-medium flex items-center gap-2">
                      <span className="text-lg">📸</span>
                      Instagram Profile
                    </Label>
                    <Input
                      value={socialLinks.instagram}
                      onChange={(e) => setSocialLinks({...socialLinks, instagram: e.target.value})}
                      placeholder="https://instagram.com/yourprofile"
                      className="mt-2 h-12 text-base"
                    />
                  </div>

                  {/* TikTok */}
                  <div>
                    <Label className="text-sm font-medium flex items-center gap-2">
                      <span className="text-lg">🎵</span>
                      TikTok Profile
                    </Label>
                    <Input
                      value={socialLinks.tiktok}
                      onChange={(e) => setSocialLinks({...socialLinks, tiktok: e.target.value})}
                      placeholder="https://tiktok.com/@yourprofile"
                      className="mt-2 h-12 text-base"
                    />
                  </div>

                  {/* Spotify */}
                  <div>
                    <Label className="text-sm font-medium flex items-center gap-2">
                      <span className="text-lg">🎧</span>
                      Spotify Profile
                    </Label>
                    <Input
                      value={socialLinks.spotify_link}
                      onChange={(e) => setSocialLinks({...socialLinks, spotify_link: e.target.value})}
                      placeholder="https://open.spotify.com/user/yourprofile"
                      className="mt-2 h-12 text-base"
                    />
                  </div>

                  {/* SoundCloud */}
                  <div>
                    <Label className="text-sm font-medium flex items-center gap-2">
                      <span className="text-lg">🔊</span>
                      SoundCloud Profile
                    </Label>
                    <Input
                      value={socialLinks.soundcloud || ''}
                      onChange={(e) => setSocialLinks({...socialLinks, soundcloud: e.target.value})}
                      placeholder="https://soundcloud.com/yourprofile"
                      className="mt-2 h-12 text-base"
                    />
                  </div>

                  {/* YouTube */}
                  <div>
                    <Label className="text-sm font-medium flex items-center gap-2">
                      <span className="text-lg">🎬</span>
                      YouTube Channel
                    </Label>
                    <Input
                      value={socialLinks.youtube || ''}
                      onChange={(e) => setSocialLinks({...socialLinks, youtube: e.target.value})}
                      placeholder="https://youtube.com/@yourchannel"
                      className="mt-2 h-12 text-base"
                    />
                  </div>

                  {/* Save Button */}
                  <Button 
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('access_token');
                        
                        // Only send social links that are editable in this section
                        // Cash App and Venmo are managed in Settings, not here
                        const socialLinksToSave = {
                          twitch: socialLinks.twitch,
                          tiktok: socialLinks.tiktok,
                          instagram: socialLinks.instagram,
                          spotify_link: socialLinks.spotify_link,
                          soundcloud: socialLinks.soundcloud,
                          youtube: socialLinks.youtube
                        };
                        
                        await axios.put(
                          `${BACKEND_URL}/api/auth/update-profile`,
                          socialLinksToSave,
                          { headers: { 'Authorization': `Bearer ${token}` } }
                        );
                        
                        // Refetch user data from backend to ensure we have the latest saved values
                        const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
                          headers: { 'Authorization': `Bearer ${token}` }
                        });
                        
                        const freshUser = response.data;
                        localStorage.setItem('user', JSON.stringify(freshUser));
                        setUser(freshUser);
                        
                        // Update socialLinks state with the fresh data from backend
                        setSocialLinks({
                          cash_app: freshUser.cash_app || '',
                          venmo: freshUser.venmo || '',
                          twitch: freshUser.twitch || '',
                          tiktok: freshUser.tiktok || '',
                          instagram: freshUser.instagram || '',
                          spotify_link: freshUser.spotify_link || '',
                          soundcloud: freshUser.soundcloud || '',
                          youtube: freshUser.youtube || ''
                        });

                        toast({
                          title: "Social links saved",
                          description: "Your social profiles have been updated."
                        });
                      } catch (error) {
                        console.error('Error saving social links:', error);
                        toast({
                          title: "Error",
                          description: error.response?.data?.detail || "Failed to save social links.",
                          variant: "destructive"
                        });
                      }
                    }}
                    className="w-full bg-[#FFCC00] hover:bg-[#FFCC00]/90 text-black font-semibold h-12"
                  >
                    Save Social Links
                  </Button>
                </div>
              </Card>
            </div>
          </TabsContent>

          {/* Sessions Tab */}
          <TabsContent value="sessions" id="panel-sessions" data-tour="panel-sessions" role="tabpanel" aria-labelledby="tab-sessions">
            <Card className="p-6 border-2 border-gray-200">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold font-montserrat">Past Sessions</h3>
                {(() => {
                  const canStart = canStartSessions(user);
                  return (
                    <Button 
                      className={canStart 
                        ? "bg-[#FFCC00] hover:bg-[#FFCC00]/90 text-black" 
                        : "bg-gray-300 text-gray-500 cursor-not-allowed"
                      }
                      onClick={canStart ? handleStartNewSession : undefined}
                      disabled={!canStart}
                      title={!canStart ? "Complete steps 1-4 first (username, photo, bio, payment)" : ""}
                      data-testid="sessions-tab-start-button"
                    >
                      <Play className="h-4 w-4 mr-2" />
                      Start New Session
                    </Button>
                  );
                })()}
              </div>

              {sessions.length > 0 ? (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-100 border-b-2 border-gray-200">
                        <tr>
                          <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Session #</th>
                          <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Date</th>
                          <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Submissions</th>
                          <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Status</th>
                          <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Earnings</th>
                          <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Actions</th>
                        </tr>
                      </thead>
                    <tbody className="divide-y divide-gray-200">
                      {sessions.map((session) => (
                        <tr key={session.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                            {editingSessionId === session.session_id ? (
                              <div className="flex items-center gap-2">
                                <input
                                  type="text"
                                  value={editingSessionName}
                                  onChange={(e) => setEditingSessionName(e.target.value)}
                                  className="border border-[#FFCC00] rounded px-2 py-1 text-sm flex-1"
                                  autoFocus
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      handleRenameSession(session.session_id, editingSessionName);
                                    } else if (e.key === 'Escape') {
                                      setEditingSessionId(null);
                                      setEditingSessionName('');
                                    }
                                  }}
                                />
                                <button
                                  onClick={() => handleRenameSession(session.session_id, editingSessionName)}
                                  className="text-green-600 hover:text-green-700"
                                >
                                  <Check className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => {
                                    setEditingSessionId(null);
                                    setEditingSessionName('');
                                  }}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <span>{session.session_name || `Session ${safeNumber(session.session_number, 1)}`}</span>
                                <button
                                  onClick={() => {
                                    setEditingSessionId(session.session_id);
                                    setEditingSessionName(session.session_name || `Session ${safeNumber(session.session_number, 1)}`);
                                  }}
                                  className="text-gray-400 hover:text-[#FFCC00] transition"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            <div className="flex items-center">
                              <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                              {new Date(session.start_time).toLocaleDateString()}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">{safeNumber(session.total_submissions, 0)} tracks</td>
                          <td className="px-6 py-4 text-sm">
                            {session.status === 'active' || session.is_active ? (
                              <Badge className="bg-green-500 text-white">Active</Badge>
                            ) : (
                              <Badge className="bg-gray-400 text-white">Ended</Badge>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm font-semibold text-green-600">{safeCurrency(session.total_earnings)}</td>
                          <td className="px-6 py-4">
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="border-2"
                              onClick={() => navigate(`/session/${session.session_id}`)}
                            >
                              View Session
                            </Button>
                          </td>
                        </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Pagination Controls */}
                  {sessionHasMore && (
                    <div className="mt-6 flex flex-col items-center gap-3">
                      <p className="text-sm text-gray-600">
                        Showing {sessions.length} of {sessionTotal} sessions
                      </p>
                      <Button
                        onClick={loadMoreSessions}
                        disabled={loadingMoreSessions}
                        variant="outline"
                        className="border-2 border-[#FFCC00] hover:bg-[#FFCC00] hover:text-black"
                      >
                        {loadingMoreSessions ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900 mr-2"></div>
                            Loading...
                          </>
                        ) : (
                          <>Load More Sessions</>
                        )}
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <Calendar className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-lg font-semibold mb-2">No sessions yet</p>
                  <p className="text-sm mb-4">Click &quot;Start Session&quot; to begin your first review session!</p>
                  <p className="text-xs text-gray-400">💡 Sessions are created automatically when artists submit music to you while live.</p>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Emails Tab - My Artists */}
          <TabsContent value="emails" id="panel-emails" data-tour="panel-emails" role="tabpanel" aria-labelledby="tab-emails">
            <Card className="p-6 border-2 border-gray-200" data-testid="emails-tab-content">
              {/* Header with stats and export button */}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <div>
                  <h3 className="text-xl font-bold font-montserrat flex items-center gap-2">
                    <Users className="h-5 w-5 text-[#FFCC00]" />
                    My Artists
                  </h3>
                  <p className="text-gray-600 text-sm mt-1">
                    Artists who have submitted tracks to you
                  </p>
                </div>
                
                {/* Export Button - PRO Feature */}
                <div className="relative">
                  <Button
                    onClick={handleExportEmails}
                    disabled={!emailsCanExport}
                    data-testid="export-emails-btn"
                    className={`flex items-center gap-2 ${
                      emailsCanExport 
                        ? 'bg-[#FFCC00] hover:bg-[#FFCC00]/90 text-black' 
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    <Download className="h-4 w-4" />
                    Export CSV
                    {!emailsCanExport && <Lock className="h-3 w-3 ml-1" />}
                  </Button>
                  {!emailsCanExport && (
                    <p className="text-xs text-gray-500 mt-1 text-center">
                      <Crown className="h-3 w-3 inline mr-1 text-[#FFCC00]" />
                      PRO feature
                    </p>
                  )}
                </div>
              </div>
              
              {/* Stats Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-black">{emailsStats.total_unique_emails}</p>
                  <p className="text-xs text-gray-500">Unique Artists</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-black">{emailsStats.total_submissions}</p>
                  <p className="text-xs text-gray-500">Total Submissions</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-[#FFCC00]">{emailsStats.total_paid_submissions || 0}</p>
                  <p className="text-xs text-gray-500">Paid Submissions</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-green-600">{emailsStats.loyal_supporters || 0}</p>
                  <p className="text-xs text-gray-500">Loyal Supporters</p>
                </div>
              </div>

              {/* Loading State */}
              {emailsLoading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FFCC00] mx-auto mb-3"></div>
                  <p className="text-gray-500">Loading your artists...</p>
                </div>
              ) : collectedEmails.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="emails-table">
                    <thead className="bg-gray-100 border-b-2 border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Artist</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Email</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Tracks</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 hidden sm:table-cell">First Submission</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 hidden md:table-cell">Last Submission</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {collectedEmails
                        .slice((emailsPage - 1) * EMAILS_PER_PAGE, emailsPage * EMAILS_PER_PAGE)
                        .map((artist, index) => (
                        <tr key={artist.email} className="hover:bg-gray-50" data-testid={`artist-row-${index}`}>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="h-8 w-8 rounded-full bg-[#FFCC00]/20 flex items-center justify-center">
                                <span className="text-sm font-bold text-[#FFCC00]">
                                  {artist.artist_name?.charAt(0)?.toUpperCase() || '?'}
                                </span>
                              </div>
                              <span className="text-sm font-medium text-gray-900">{artist.artist_name || 'Unknown'}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">{artist.email}</td>
                          <td className="px-4 py-3 text-center">
                            <span className={`inline-flex items-center justify-center min-w-[2rem] px-2 py-1 rounded-full text-xs font-bold ${
                              artist.tracks_submitted >= 5 
                                ? 'bg-green-100 text-green-700' 
                                : artist.tracks_submitted >= 3 
                                  ? 'bg-[#FFCC00]/20 text-[#FFCC00]' 
                                  : 'bg-gray-100 text-gray-600'
                            }`}>
                              {artist.tracks_submitted}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500 hidden sm:table-cell">
                            {artist.first_submission ? new Date(artist.first_submission).toLocaleDateString() : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500 hidden md:table-cell">
                            {artist.last_submission ? new Date(artist.last_submission).toLocaleDateString() : '-'}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {artist.is_supporter ? (
                              <Badge className="bg-green-100 text-green-700 border-green-200">
                                <Star className="h-3 w-3 mr-1" />
                                Supporter
                              </Badge>
                            ) : artist.paid_submissions > 0 ? (
                              <Badge className="bg-[#FFCC00]/20 text-[#FFCC00] border-[#FFCC00]/30">
                                {artist.paid_submissions} paid
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-gray-400 border-gray-200">
                                Free
                              </Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  {/* Pagination Controls */}
                  {collectedEmails.length > EMAILS_PER_PAGE && (
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
                      <p className="text-sm text-gray-500">
                        Showing {((emailsPage - 1) * EMAILS_PER_PAGE) + 1} - {Math.min(emailsPage * EMAILS_PER_PAGE, collectedEmails.length)} of {collectedEmails.length} artists
                      </p>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEmailsPage(prev => Math.max(prev - 1, 1))}
                          disabled={emailsPage === 1}
                          className="px-3"
                        >
                          <ChevronUp className="h-4 w-4 rotate-[-90deg]" />
                          Prev
                        </Button>
                        <span className="text-sm text-gray-600 px-2">
                          Page {emailsPage} of {Math.ceil(collectedEmails.length / EMAILS_PER_PAGE)}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEmailsPage(prev => Math.min(prev + 1, Math.ceil(collectedEmails.length / EMAILS_PER_PAGE)))}
                          disabled={emailsPage >= Math.ceil(collectedEmails.length / EMAILS_PER_PAGE)}
                          className="px-3"
                        >
                          Next
                          <ChevronDown className="h-4 w-4 rotate-[-90deg]" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-lg font-semibold mb-2">No artists yet</p>
                  <p className="text-sm">When artists submit tracks to you, their emails will appear here.</p>
                  <p className="text-xs text-gray-400 mt-2">💡 Share your reviewer profile link to start collecting submissions!</p>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Notifications Tab - PRO Feature (enabled for PRO subscribers) */}
          <TabsContent value="notifications" id="panel-notifications" data-tour="panel-notifications" role="tabpanel" aria-labelledby="tab-notifications">
            <Card className="p-6 border-2 border-gray-200 relative overflow-hidden">
              {/* Notifications content - Email is FREE for all users, SMS coming soon */}
              <h3 className="text-xl font-bold font-montserrat mb-4 flex items-center">
                <Bell className="h-5 w-5 mr-2" />
                Live Notifications
              </h3>
              <p className="text-gray-600 mb-6">
                Notify your followers when you start a session. They&apos;ll receive an email with a direct link to submit their track.
              </p>
              
              <div className="bg-gradient-to-r from-[#F2D857]/10 to-transparent p-6 rounded-lg border-2 border-[#F2D857] mb-6">
                <div className="flex items-start gap-4">
                  <div className="bg-[#F2D857] p-3 rounded-full">
                    <Bell className="h-6 w-6 text-black" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-lg mb-2">Send &quot;I&apos;m Live&quot; Notification</h4>
                    <p className="text-gray-600 mb-4">
                      Instantly notify all your followers via email when you start accepting submissions.
                    </p>
                    <Button 
                      className="bg-[#F2D857] hover:bg-[#F2D857]/90 text-black font-bold"
                      onClick={handleSendLiveNotification}
                      disabled={sendingNotification || !activeSession}
                      data-testid="send-notification-btn"
                    >
                      {sendingNotification ? 'Sending...' : 'Send Live Notification'}
                    </Button>
                    {!activeSession && (
                      <p className="text-sm text-red-600 mt-2">
                        You must have an active session with submissions enabled to send notifications.
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="font-semibold">Your Followers</h4>
                <p className="text-sm text-gray-600">
                  You have <span className="font-bold">{user?.followersCount || user?.followers || 0}</span> followers who will receive notifications.
                </p>
                
                <Separator />
                
                <div>
                  <h4 className="font-semibold mb-2">Notification Settings</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                      <div>
                        <p className="font-medium">Auto-notify on session start</p>
                        <p className="text-sm text-gray-500">Automatically send email notifications when you start a new session</p>
                      </div>
                      <Switch 
                        checked={notificationSettings.auto_notify_on_session_start}
                        onCheckedChange={(checked) => handleUpdateNotificationSetting('auto_notify_on_session_start', checked)}
                        data-testid="auto-notify-toggle"
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                      <div>
                        <p className="font-medium">Include queue count</p>
                        <p className="text-sm text-gray-500">Automatically add &quot;Queue: X tracks&quot; to your notification messages</p>
                      </div>
                      <Switch 
                        checked={notificationSettings.include_queue_count}
                        onCheckedChange={(checked) => handleUpdateNotificationSetting('include_queue_count', checked)}
                        data-testid="include-queue-toggle"
                      />
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Custom Message Templates - Collapsible Dropdown */}
                <div>
                  <button 
                    onClick={() => setCustomMessagesExpanded(!customMessagesExpanded)}
                    className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <h4 className="font-semibold">Custom Notification Messages</h4>
                    {customMessagesExpanded ? (
                      <ChevronUp className="h-5 w-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-500" />
                    )}
                  </button>
                  
                  {/* Collapsible Content */}
                  {customMessagesExpanded && (
                    <div className="mt-4 space-y-4 animate-in slide-in-from-top-2 duration-200">
                      <p className="text-sm text-gray-500">
                        Personalize your notification messages. Available placeholders:
                      </p>
                      <ul className="text-sm text-gray-500 ml-4 list-disc">
                        <li><code className="bg-gray-100 px-1 rounded">{'{reviewer_name}'}</code> - Your display name</li>
                        <li><code className="bg-gray-100 px-1 rounded">{'{session_url}'}</code> - Direct link to your submission page</li>
                      </ul>
                      <p className="text-xs text-amber-600">
                        💡 Queue count is automatically added to messages when the &quot;Include queue count&quot; toggle above is ON.
                      </p>
                      
                      {/* SMS Message - Coming Soon */}
                      <div className="p-4 bg-gray-100 rounded-lg relative opacity-60">
                        <div className="absolute top-2 right-2 flex items-center gap-1 bg-amber-100 text-amber-700 text-xs font-medium px-2 py-1 rounded">
                          <Clock className="w-3 h-3" />
                          SMS Coming Soon
                        </div>
                        <Label className="text-sm font-medium mb-2 block text-gray-400">
                          📱 Custom SMS Message
                        </Label>
                        <Textarea
                          placeholder="🎤 {reviewer_name} is LIVE on Mic Check Millionaire!"
                          value={notificationSettings.custom_sms_message}
                          className="min-h-[80px] text-sm bg-gray-200"
                          maxLength={500}
                          disabled={true}
                        />
                        <p className="text-xs text-gray-400 mt-1">
                          SMS notifications will be available as a PRO feature soon!
                        </p>
                      </div>

                      {/* Email Subject - Enabled for all */}
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <Label className="text-sm font-medium mb-2 block">
                          📧 Custom Email Subject
                        </Label>
                        <Input
                          placeholder="🎤 {reviewer_name} is LIVE - Submit Your Track Now!"
                          value={notificationSettings.custom_email_subject}
                          onChange={(e) => setNotificationSettings(prev => ({ ...prev, custom_email_subject: e.target.value }))}
                          className="text-sm"
                          maxLength={200}
                        />
                        <p className="text-xs text-gray-400 mt-1">
                          {notificationSettings.custom_email_subject?.length || 0}/200 characters • <span className="text-gray-500">Leave empty to use default</span>
                        </p>
                      </div>

                      {/* Email Body Message - Enabled for all */}
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <Label className="text-sm font-medium mb-2 block">
                          📧 Custom Email Message
                        </Label>
                        <Textarea
                          placeholder="Hey there! 👋&#10;&#10;{reviewer_name} just went live and is ready to review tracks.&#10;&#10;This is your chance to get professional feedback on your music. Don&apos;t miss out - spots fill up fast!"
                          value={notificationSettings.custom_email_message}
                          onChange={(e) => setNotificationSettings(prev => ({ ...prev, custom_email_message: e.target.value }))}
                          className="min-h-[100px] text-sm"
                          maxLength={2000}
                        />
                        <p className="text-xs text-gray-400 mt-1">
                          {notificationSettings.custom_email_message?.length || 0}/2000 characters • <span className="text-gray-500">Leave empty to use default message above</span>
                        </p>
                      </div>

                      <Button 
                        onClick={handleSaveCustomMessages}
                        disabled={savingCustomMessage}
                        className="w-full bg-[#F2D857] hover:bg-[#F2D857]/90 text-black font-semibold"
                        data-testid="save-custom-messages-btn"
                      >
                        {savingCustomMessage ? 'Saving...' : 'Save Custom Messages'}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" id="panel-settings" data-tour="panel-settings" role="tabpanel" aria-labelledby="tab-settings">
            <div className="grid gap-6">
              {/* Monetization Plan - TOP OF SETTINGS */}
              <Card className="p-6 border-2 border-[#FFCC00] bg-gradient-to-br from-yellow-50 to-white">
                <h3 className="text-xl font-bold font-montserrat mb-4 flex items-center">
                  <DollarSign className="h-5 w-5 mr-2 text-[#FFCC00]" />
                  Monetization Plan
                </h3>
                
                <div className="space-y-4">
                  {/* Current Plan Display */}
                  <div className="bg-white p-4 rounded-lg border-2 border-gray-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-sm text-gray-600 mb-1">Current Plan:</p>
                        {monetizationPlan === 'subscription_cashapp_venmo' ? (
                          <>
                            <p className="text-lg font-bold text-gray-900 mb-2">
                              {user?.subscription_status === 'trialing' ? (
                                <>🎁 Pro Plan <span className="text-amber-600">(Free Trial)</span></>
                              ) : (
                                <>Pro Plan ($10/month)</>
                              )}
                            </p>
                            <p className="text-sm text-gray-600">
                              ✓ Keep 100% of your tips<br />
                              ✓ Direct tips via Cash App & Venmo<br />
                              ✓ No platform fees
                            </p>
                          </>
                        ) : monetizationPlan === 'free_stripe_10percent' ? (
                          <>
                            <p className="text-lg font-bold text-gray-900 mb-2">Free Plan (Rev-Share)</p>
                            <p className="text-sm text-gray-600">
                              ✓ No monthly fee<br />
                              ✓ Tips processed via Stripe<br />
                              ✓ You receive 90%, MCM takes 10%
                            </p>
                          </>
                        ) : (
                          <p className="text-lg font-bold text-gray-900">No plan selected</p>
                        )}
                      </div>
                      <Button
                        onClick={() => setShowMonetizationModal(true)}
                        className="bg-[#FFCC00] hover:bg-[#FFCC00]/90 text-black font-semibold"
                      >
                        Change Plan
                      </Button>
                    </div>
                  </div>

                  {/* Plan-Specific Info */}
                  {monetizationPlan === 'subscription_cashapp_venmo' && (
                    <div className={`${user?.subscription_status === 'trialing' ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-200'} border rounded-lg p-4`}>
                      <p className={`text-sm ${user?.subscription_status === 'trialing' ? 'text-amber-800' : 'text-blue-800'}`}>
                        {user?.subscription_status === 'trialing' ? (
                          <>
                            <strong>🎁 Free Trial Active!</strong> You&apos;re enjoying your 30-day Pro Plan trial. Keep 100% of your earnings with no platform fees. Your trial will convert to $10/month after the trial period.
                          </>
                        ) : (
                          <>
                            <strong>Pro Plan Active:</strong> Make sure to add your Cash App and Venmo handles in the Profile tab so artists can tip you directly.
                          </>
                        )}
                      </p>
                    </div>
                  )}
                  
                  {monetizationPlan === 'free_stripe_10percent' && (
                    <div className={`${user?.stripe_details_submitted || user?.monetization_setup_complete ? 'bg-green-50 border-green-200' : 'bg-purple-50 border-purple-200'} border rounded-lg p-4`}>
                      <p className={`text-sm ${user?.stripe_details_submitted || user?.monetization_setup_complete ? 'text-green-800' : 'text-purple-800'}`}>
                        {user?.stripe_details_submitted || user?.monetization_setup_complete ? (
                          <>
                            <strong>✅ Stripe Connected!</strong> You&apos;re all set to receive tips. You keep 90% of all tips (10% platform fee).
                          </>
                        ) : (
                          <>
                            <strong>Free Plan Active:</strong> Connect your Stripe account below to start receiving tips. Platform fee: 10% (you keep 90%).
                          </>
                        )}
                      </p>
                    </div>
                  )}
                </div>
              </Card>

              {/* Plan-Specific Settings (Pro Plan or Free Plan) */}
              {monetizationPlan === 'subscription_cashapp_venmo' && (
                <SubscriptionSettings 
                  user={user} 
                  onUserUpdate={(updatedUser) => {
                    setUser(updatedUser);
                    localStorage.setItem('user', JSON.stringify(updatedUser));
                  }}
                />
              )}
              
              {monetizationPlan === 'free_stripe_10percent' && (
                <RevShareSettings 
                  user={user}
                  onUserUpdate={(updatedUser) => {
                    setUser(updatedUser);
                    localStorage.setItem('user', JSON.stringify(updatedUser));
                  }}
                />
              )}

              {/* Submission Pricing */}
              <Card className="p-6 border-2 border-gray-200">
                <h3 className="text-xl font-bold font-montserrat mb-4 flex items-center">
                  <DollarSign className="h-5 w-5 mr-2 text-[#FFCC00]" />
                  Submission Pricing
                </h3>
                <p className="text-gray-600 mb-4">Enable priority queue features for artists</p>
                <p className="text-xs text-gray-500 mb-6">
                  These settings control how artists can pay to prioritize their submissions. Your payout method is configured in your monetization plan above.
                </p>

                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">Skip Line Payments</p>
                      <p className="text-sm text-gray-600">Artists pay to move up in queue</p>
                    </div>
                    <Switch
                      checked={settings.skipLineEnabled}
                      onCheckedChange={(checked) => setSettings({ ...settings, skipLineEnabled: checked })}
                    />
                  </div>

                  {user?.email === 'jxhn@miccheckm.com' && (
                    <div className="flex items-center justify-between p-4 bg-yellow-50 border-2 border-yellow-300 rounded-lg">
                      <div>
                        <p className="font-medium flex items-center">
                          Twitch Subscription Skip
                          <Badge className="ml-2 bg-[#FFCC00] text-black">Admin Only</Badge>
                        </p>
                        <p className="text-sm text-gray-600">Twitch subs can skip the line</p>
                      </div>
                      <Switch
                        checked={settings.twitchSubSkip}
                        onCheckedChange={(checked) => setSettings({ ...settings, twitchSubSkip: checked })}
                      />
                    </div>
                  )}
                </div>
              </Card>

              {/* Submission Types - Collapsible */}
              <Card className="p-6 border-2 border-gray-200">
                <button 
                  onClick={() => setSubmissionTypesExpanded(!submissionTypesExpanded)}
                  className="w-full flex items-center justify-between"
                >
                  <div className="flex items-center">
                    <Music className="h-5 w-5 mr-2 text-[#FFCC00]" />
                    <h3 className="text-xl font-bold font-montserrat">Submission Types</h3>
                  </div>
                  {submissionTypesExpanded ? (
                    <ChevronUp className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-500" />
                  )}
                </button>
                
                {submissionTypesExpanded && (
                  <div className="mt-4 animate-in slide-in-from-top-2 duration-200">
                    <p className="text-gray-600 mb-4">Choose which submission methods artists can use</p>

                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium">Spotify Links</p>
                          <p className="text-sm text-gray-600">Allow Spotify track submissions</p>
                        </div>
                        <Switch
                          checked={settings.allowSpotify}
                          onCheckedChange={(checked) => {
                            if (!checked) {
                              handleProFeatureToggle("Disabling Spotify Links");
                            }
                          }}
                        />
                      </div>

                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium">SoundCloud Links</p>
                          <p className="text-sm text-gray-600">Allow SoundCloud track submissions</p>
                        </div>
                        <Switch
                          checked={settings.allowSoundcloud}
                          onCheckedChange={(checked) => {
                            if (!checked) {
                              handleProFeatureToggle("Disabling SoundCloud Links");
                            }
                          }}
                        />
                      </div>

                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium">YouTube Links</p>
                          <p className="text-sm text-gray-600">Allow YouTube video submissions</p>
                        </div>
                        <Switch
                          checked={settings.allowYoutube}
                          onCheckedChange={(checked) => {
                            if (!checked) {
                              handleProFeatureToggle("Disabling YouTube Links");
                            }
                          }}
                        />
                      </div>

                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium">File Uploads</p>
                          <p className="text-sm text-gray-600">Allow MP3, WAV, M4A file uploads</p>
                        </div>
                        <Switch
                          checked={settings.allowFileUploads}
                          onCheckedChange={(checked) => {
                            if (!checked) {
                              handleProFeatureToggle("Disabling File Uploads");
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </Card>

              {/* Submission Gates - Collapsible */}
              <Card className="p-6 border-2 border-gray-200">
                <button 
                  onClick={() => setSubmissionGatesExpanded(!submissionGatesExpanded)}
                  className="w-full flex items-center justify-between"
                >
                  <div className="flex items-center">
                    <CheckCircle className="h-5 w-5 mr-2 text-[#FFCC00]" />
                    <h3 className="text-xl font-bold font-montserrat">Submission Gates</h3>
                  </div>
                  {submissionGatesExpanded ? (
                    <ChevronUp className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-500" />
                  )}
                </button>
                
                {submissionGatesExpanded && (
                  <div className="mt-4 animate-in slide-in-from-top-2 duration-200">
                    <p className="text-gray-600 mb-4">Require artists to complete actions before submitting</p>

                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium">Require Email Address</p>
                          <p className="text-sm text-gray-600">Build your email list</p>
                        </div>
                        <Switch
                          checked={settings.requireEmail}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              handleProFeatureToggle("Require Email Address");
                            }
                          }}
                        />
                      </div>

                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium">Require Spotify Follow</p>
                          <p className="text-sm text-gray-600">Artists must follow your Spotify</p>
                        </div>
                        <Switch
                          checked={settings.requireSpotifyFollow}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              handleProFeatureToggle("Require Spotify Follow");
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </Card>

              {/* Danger Zone - Delete Account */}
              <Card className="p-6 border-2 border-red-300 bg-red-50">
                <h3 className="text-xl font-bold font-montserrat mb-4 flex items-center text-red-700">
                  <AlertTriangle className="h-5 w-5 mr-2" />
                  Danger Zone
                </h3>
                <p className="text-gray-700 mb-4">
                  Once you delete your account, there is no going back. This will permanently delete:
                </p>
                <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1 ml-2">
                  <li>Your reviewer profile and all settings</li>
                  <li>All your sessions and reviews</li>
                  <li>Your Stripe Connect account (if connected)</li>
                  <li>All track submissions you&apos;ve made</li>
                </ul>
                <Button
                  variant="destructive"
                  onClick={() => {
                    if (window.confirm('Are you ABSOLUTELY sure you want to delete your account? This action cannot be undone!')) {
                      handleDeleteAccount();
                    }
                  }}
                  className="bg-red-600 hover:bg-red-700 text-white font-semibold"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete My Account Permanently
                </Button>
              </Card>

              <Button
                onClick={handleSaveSettings}
                className="w-full bg-[#FFCC00] hover:bg-[#FFCC00]/90 text-black font-semibold h-12 text-lg"
              >
                Save Settings
              </Button>
            </div>
          </TabsContent>

          {/* Connect Spotify Tab */}
          <TabsContent value="connect-spotify" id="panel-connect-spotify" data-tour="panel-spotify" role="tabpanel" aria-labelledby="tab-connect-spotify">
            <div className="max-w-2xl mx-auto">
              <Card className="p-8 border-2 border-[#F2D857] bg-gradient-to-br from-white to-green-50">
                <div className="text-center mb-6">
                  <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Music className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold font-montserrat mb-2">Connect Spotify</h3>
                  <p className="text-gray-600">
                    Link your Spotify account to play tracks from this platform during your review sessions.
                  </p>
                </div>

                <div className="bg-white p-4 sm:p-6 rounded-lg border-2 border-gray-200 mb-6">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 ${spotifyConnected ? 'bg-green-500' : 'bg-gray-400'} rounded-full flex items-center justify-center flex-shrink-0`}>
                        <Music className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-center sm:text-left">
                        <p className="font-semibold text-lg">Spotify</p>
                        <p className="text-sm text-gray-500">
                          {checkingSpotifyStatus ? 'Checking...' : spotifyConnected ? 'Connected' : 'Not connected'}
                        </p>
                      </div>
                    </div>
                    {spotifyConnected ? (
                      <div className="flex gap-2 flex-col sm:flex-row w-full sm:w-auto">
                        <Button 
                          disabled
                          size="lg"
                          className="w-full sm:w-auto bg-gray-400 text-white cursor-not-allowed font-semibold"
                          aria-disabled="true"
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Connected
                        </Button>
                        <Button 
                          onClick={() => handleConnectSpotify()}
                          disabled={checkingSpotifyStatus}
                          size="lg"
                          variant="outline"
                          className="w-full sm:w-auto border-green-600 text-green-600 hover:bg-green-50 font-semibold"
                        >
                          Reconnect
                        </Button>
                      </div>
                    ) : (
                      <Button 
                        size="lg"
                        onClick={() => handleConnectSpotify()}
                        className="w-full sm:w-auto bg-green-500 hover:bg-green-600 text-white font-semibold"
                        disabled={checkingSpotifyStatus}
                      >
                        {checkingSpotifyStatus ? 'Checking...' : 'Connect Spotify'}
                      </Button>
                    )}
                  </div>
                </div>

                {spotifyConnected && (
                  <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                      <div>
                        <p className="font-semibold text-green-800 mb-1">Successfully Connected!</p>
                        <p className="text-sm text-green-700">
                          You can now play Spotify tracks during your review sessions. Artists can submit Spotify links and you&apos;ll be able to play them directly.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {!spotifyConnected && (
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <h4 className="font-semibold text-gray-800">Why connect Spotify?</h4>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>Play Spotify tracks directly in your session dashboard</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>Access full track metadata and artwork</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>Seamless playback without leaving the platform</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>Professional review experience for artists</span>
                      </li>
                    </ul>
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Avatar Crop Modal */}
      <AvatarCropModal
        isOpen={showCropModal}
        onClose={() => {
          setShowCropModal(false);
          setSelectedImageForCrop(null);
        }}
        imageSrc={selectedImageForCrop}
        onSave={handleCroppedImageSave}
      />

      {/* Monetization Plan Modal */}
      {showMonetizationModal && (
        <MonetizationPlanModal
          user={user}
          onPlanSelected={(updatedUser) => {
            // Update user state with fresh data from backend
            setUser(updatedUser);
            setMonetizationPlan(updatedUser.monetization_plan);
            setShowMonetizationModal(false);
            
            // Note: localStorage is already updated in MonetizationPlanModal
            // No need to reload - state is now correct
          }}
        />
      )}

      {/* Reviewer Guided Walkthrough - 2 Phase */}
      <ReviewerGuidedTour 
        user={user}
        onTabChange={setActiveTab}
      />
    </div>
  );
};

export default ReviewerDashboard;
